import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaSearch, FaMapMarkerAlt, FaFilter, FaTimes, FaBriefcase } from 'react-icons/fa';
import { jobsAPI } from '../utils/api';
import JobCard from '../components/JobCard';
import './Jobs.css';

const COUNTRIES = ['UAE', 'Saudi Arabia', 'Kuwait', 'Qatar', 'Bahrain', 'Oman', 'Singapore', 'Malaysia', 'Libya'];
const CATEGORIES = ['Engineering', 'Healthcare', 'Hospitality', 'Labour', 'Technology', 'Finance', 'Logistics', 'Transport', 'Education', 'Agriculture', 'Construction'];
const COUNTRY_FLAGS = { 'UAE': '🇦🇪', 'Saudi Arabia': '🇸🇦', 'Kuwait': '🇰🇼', 'Qatar': '🇶🇦', 'Bahrain': '🇧🇭', 'Oman': '🇴🇲', 'Singapore': '🇸🇬', 'Malaysia': '🇲🇾', 'Libya': '🇱🇾' };

const Jobs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [country, setCountry] = useState(searchParams.get('country') || '');
  const [category, setCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const limit = 12;

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await jobsAPI.getAll({ search, country, category, page, limit });
      setJobs(res.data.jobs);
      setTotal(res.data.total);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [search, country, category, page]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchParams({ ...(search && { search }), ...(country && { country }) });
  };

  const clearFilters = () => { setSearch(''); setCountry(''); setCategory(''); setPage(1); };
  const totalPages = Math.ceil(total / limit);
  const hasFilters = search || country || category;

  return (
    <div className="jobs-page">
      <div className="jobs-hero">
        <div className="container">
          <div className="jobs-hero-badge"><FaBriefcase /> Open Positions</div>
          <h1>Find Your Dream Job <span>Abroad</span></h1>
          <p>Explore verified international job opportunities across 9 countries in the Gulf and beyond.</p>

          <form className="jobs-search-bar" onSubmit={handleSearch}>
            <div className="search-inp-wrap">
              <FaSearch />
              <input
                type="text" placeholder="Search job role, skill..."
                value={search} onChange={e => setSearch(e.target.value)}
                className="search-inp"
              />
              {search && <button type="button" onClick={() => setSearch('')} className="clear-btn"><FaTimes /></button>}
            </div>
            <div className="search-inp-wrap">
              <FaMapMarkerAlt />
              <select value={country} onChange={e => setCountry(e.target.value)} className="search-inp">
                <option value="">All Countries</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{COUNTRY_FLAGS[c]} {c}</option>)}
              </select>
            </div>
            <button type="submit" className="btn-search-go">Search</button>
            <button type="button" className="btn-filter-toggle" onClick={() => setShowFilters(!showFilters)}>
              <FaFilter /> Filters
            </button>
          </form>

          {showFilters && (
            <div className="filter-bar">
              <div className="filter-group">
                <label>Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)} className="filter-select">
                  <option value="">All Categories</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {hasFilters && <button onClick={clearFilters} className="clear-all-btn"><FaTimes /> Clear All</button>}
            </div>
          )}
        </div>
      </div>

      <div className="container">
        <div className="jobs-count-bar">
          <span className="jobs-count">{loading ? 'Loading...' : `${total} job${total !== 1 ? 's' : ''} found`}</span>
          <div className="active-filters">
            {search && <span className="filter-chip">{search} <button onClick={() => setSearch('')}><FaTimes /></button></span>}
            {country && <span className="filter-chip">{COUNTRY_FLAGS[country]} {country} <button onClick={() => setCountry('')}><FaTimes /></button></span>}
            {category && <span className="filter-chip">{category} <button onClick={() => setCategory('')}><FaTimes /></button></span>}
          </div>
        </div>

        {loading ? (
          <div className="loading-wrap"><div className="spinner" /><p>Loading jobs...</p></div>
        ) : jobs.length > 0 ? (
          <>
            <div className="jobs-grid-page">
              {jobs.map(job => <JobCard key={job.id} job={job} />)}
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="page-btn">← Prev</button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)} className={`page-btn${page === p ? ' active' : ''}`}>{p}</button>
                ))}
                <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="page-btn">Next →</button>
              </div>
            )}
          </>
        ) : (
          <div className="no-jobs">
            <FaBriefcase size={56} />
            <h3>No jobs found</h3>
            <p>Try adjusting your search criteria or check back later for new openings.</p>
            <button onClick={clearFilters} className="btn-primary">Clear Filters</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Jobs;
